import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import { Box, Typography, useTheme } from "@mui/material"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import { AvatarContentCell } from "./applicationTable"
import {
    APPLICATION_AMENDABLE_STATUSES,
    InstallerApplicationStatuses,
} from "../../../constants/enums"
import {
    TT_AMEND_APPLICATION,
    TT_CX_SUBMIT,
    TT_UNREAD_COMMENTS,
} from "../../../constants/tooltips"
import { useGetUserData } from "../../../hooks/useGetUserData"
import { useScreenSize } from "../../../hooks/useScreenSize"
import {
    AMEND_SINGLE_APPLICATION_BASE,
    COMMISSIONING_FORM_BASE,
    INSTALLER_SINGLE_APPLICATION_BASE,
} from "../../../router/routes"
import { IListApplication } from "../../../services/middleware/types/installerInterface/installerApplicationTypes"
import { formatDate, formatUnixDate } from "../../../utils/general/generalUtils"
import { scrollToTop } from "../../../utils/submitApplication/submitApplicationUtils"
import AppButton from "../../buttons/appButton"
import CopyTypography from "../../dataDisplay/typography/copyTypography"
import AppTooltip from "../../helper/appTooltip"
import LctChip from "../../pills/lct/lctChip"
import StatusChip from "../../pills/status/statusChip"
import DisplayIf from "../../wrappers/displayIf"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

const ADDRESS_MAX_LENGTH = 25

interface IApplicationRowProps {
    application: IListApplication
}
/**
 * Single row in my InstallerApplicationTable for Installers to view their applications.
 * Includes logic for opening the application.
 *
 * Any changes to XL only config must be aligned with headings in
 * `InstallerApplicationTable.tsx`
 */
const InstallerApplicationRow = ({
    application,
}: IApplicationRowProps): ReactElement => {
    const navigate = useNavigate()
    const { isXl } = useScreenSize()
    const { isInstallerAdmin } = useGetUserData()
    const theme = useTheme()

    const addressAsString =
        String(application.installerCustomerDetails.mainAddress) ?? ""

    const addressTruncated =
        addressAsString.length > ADDRESS_MAX_LENGTH
            ? `${addressAsString.substring(0, ADDRESS_MAX_LENGTH)}...`
            : addressAsString

    return (
        <AppTableContentRow
            key={application.applicationId}
            hover
            onClick={() => {
                navigate(
                    `${INSTALLER_SINGLE_APPLICATION_BASE}/${application.applicationId}`
                )
                scrollToTop(document)
            }}
            sx={{
                cursor: "pointer",
            }}
        >
            <AvatarContentCell lcts={application.lcts} />

            <AppTableCell>
                <CopyTypography
                    text={addressTruncated}
                    customCopyText={addressAsString}
                    typographyProps={{ variant: "body2" }}
                    customTooltip={`${addressAsString} - click to copy to clipboard!`}
                />
            </AppTableCell>
            <AppTableCell>
                {application.installerCustomerDetails.installationPostCode}
            </AppTableCell>
            {isInstallerAdmin && (
                <AppTableCell>
                    {application.installerCustomerDetails.installerName}
                </AppTableCell>
            )}
            <DisplayIf condition={isXl}>
                <AppTableCell>
                    <CopyTypography
                        typographyProps={{ variant: "body2" }}
                        customCopyText={application.applicationId}
                        text={`${application.applicationId.substring(
                            0,
                            17
                        )}...`}
                        customTooltip={`${application.applicationId} - click to copy to clipboard!`}
                    />
                </AppTableCell>
            </DisplayIf>
            <AppTableCell>{formatDate(application.createdDate)}</AppTableCell>
            <AppTableCell>
                {application.installerLastUpdated
                    ? formatUnixDate(application.installerLastUpdated)
                    : formatDate(application.createdDate)}
            </AppTableCell>
            <AppTableCell align="center">
                <StatusChip status={application.applicationStatus} />
            </AppTableCell>
            <DisplayIf condition={isXl}>
                <AppTableCell>
                    {application.lcts.map((lct) => (
                        <LctChip
                            key={lct}
                            lct={lct}
                            sx={{
                                mr: 1,
                                mb: application.lcts.length > 0 ? 1 : 0,
                            }}
                        />
                    ))}
                </AppTableCell>
            </DisplayIf>
            <AppTableCell>
                {application.isReadyForCx ? (
                    <AppTooltip title={TT_CX_SUBMIT}>
                        <AppButton
                            endIcon={<NavigateNextIcon />}
                            variant="contained"
                            color="warning"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                navigate(
                                    `${COMMISSIONING_FORM_BASE}/${application.applicationId}`
                                )
                                scrollToTop(document)
                            }}
                        >
                            Submit
                        </AppButton>
                    </AppTooltip>
                ) : (
                    <AppTooltip title="This application does not require any commissioning related actions at this stage">
                        <Typography marginLeft={7}>-</Typography>
                    </AppTooltip>
                )}
            </AppTableCell>

            <AppTableCell>
                {APPLICATION_AMENDABLE_STATUSES.includes(
                    application.applicationStatus as InstallerApplicationStatuses
                ) ? (
                    <AppTooltip title={TT_AMEND_APPLICATION}>
                        <AppButton
                            endIcon={<NavigateNextIcon />}
                            variant="contained"
                            color="primary"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                const amendUrl = `${AMEND_SINGLE_APPLICATION_BASE}/${application.applicationId}`
                                const queryParams = `?applicationStatus=${application.applicationStatus}`
                                navigate(amendUrl + queryParams)
                                scrollToTop(document)
                            }}
                        >
                            Amend
                        </AppButton>
                    </AppTooltip>
                ) : (
                    <AppTooltip title="This application cannot be amended at this stage">
                        <Typography marginLeft={7}>-</Typography>
                    </AppTooltip>
                )}
            </AppTableCell>
            <AppTableCell align="center" width={50}>
                {application.isInstallerThreadUnread && (
                    <AppTooltip title={TT_UNREAD_COMMENTS}>
                        <Box
                            sx={{
                                width: 14,
                                height: 14,
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: "50%",
                                display: "inline-block",
                            }}
                        />
                    </AppTooltip>
                )}
            </AppTableCell>
        </AppTableContentRow>
    )
}

export default InstallerApplicationRow
